import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { abandonedReminderApiService } from "../../../service/admin/abandonedReminderApiService";
import { cronConfigService } from "../../../service/admin/cronConfigService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import "./reminderSettings.css";

function bannerPreviewUrl(imageUrl) {
  const u = String(imageUrl || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("//")) return `https:${u}`;
  const api = process.env.REACT_APP_APIUrl || "";
  const origin = api.replace(/\/?api\/?$/i, "").replace(/\/$/, "");
  if (u.startsWith("/")) return `${origin}${u}`;
  return u;
}

function bannerMetaKey(banner, idx) {
  const id = banner?._id ? String(banner._id) : "";
  if (id) return `id:${id}`;
  return `tmp:${idx}:${String(banner?.imageUrl || "")}`;
}

function getBannerDimensionFeedback(meta) {
  const width = Number(meta?.width) || 0;
  const height = Number(meta?.height) || 0;
  if (!width || !height) {
    return { text: "Size detecting...", tone: "neutral" };
  }
  const ratio = width / height;
  if (ratio < 2.4) {
    return { text: "Too tall - will crop in email", tone: "danger" };
  }
  if (ratio > 3.8) {
    return { text: "Too wide - side crop possible", tone: "warning" };
  }
  if (width < 900 || height < 260) {
    return { text: "Low resolution", tone: "warning" };
  }
  return { text: "Good fit", tone: "good" };
}

function normalizeSettingsPayload(s) {
  if (!s) return null;
  return {
    ...s,
    firstDelayValue: Number(s?.firstDelayValue || s?.firstDelayHours || 24),
    firstDelayUnit: s?.firstDelayUnit === "minutes" ? "minutes" : "hours",
    maxProductsInEmail: Math.min(30, Math.max(1, Number(s?.maxProductsInEmail) || 8)),
    emailBanners: Array.isArray(s?.emailBanners)
      ? s.emailBanners.map((b) => ({
          ...b,
          applyTo: ["cart", "payment", "both"].includes(String(b?.applyTo || "").toLowerCase())
            ? String(b.applyTo).toLowerCase()
            : "both",
        }))
      : [],
    selectedCartBannerId: s?.selectedCartBannerId ? String(s.selectedCartBannerId) : "",
    selectedPaymentBannerId: s?.selectedPaymentBannerId ? String(s.selectedPaymentBannerId) : "",
    emailImageBaseUsed: s?.emailImageBaseUsed ? String(s.emailImageBaseUsed) : "",
    cartIntroMessage: String(
      s?.cartIntroMessage || "You still have items in your cart. Here is a quick recap."
    ),
    paymentIntroMessage: String(
      s?.paymentIntroMessage || "You were close to checkout. Your items are still saved below."
    ),
    cartEmailSubject: String(s?.cartEmailSubject || "Your cart is waiting - Importerr"),
    paymentEmailSubject: String(s?.paymentEmailSubject || "Complete your checkout - Importerr"),
    preCtaMessage: String(s?.preCtaMessage || ""),
  };
}

function formatCronText(schedule, isEnabled) {
  const raw = String(schedule || "").trim();
  if (!raw) return "cron schedule unavailable.";
  if (!isEnabled) return `cron disabled (${raw}).`;
  const everyMinutesMatch = raw.match(/^\*\/(\d+)\s+\*\s+\*\s+\*\s+\*$/);
  if (everyMinutesMatch) {
    const mins = Number(everyMinutesMatch[1]);
    if (Number.isFinite(mins) && mins > 0) {
      return `cron every ${mins} min when enabled.`;
    }
  }
  const everyHoursMatch = raw.match(/^0\s+\*\/(\d+)\s+\*\s+\*\s+\*$/);
  if (everyHoursMatch) {
    const hrs = Number(everyHoursMatch[1]);
    if (Number.isFinite(hrs) && hrs > 0) {
      return `cron every ${hrs} hour${hrs > 1 ? "s" : ""} when enabled.`;
    }
  }
  if (raw === "0 * * * *") return "cron every 1 hour when enabled.";
  if (raw === "0 0 * * *") return "cron daily at 12:00 AM when enabled.";
  return `cron ${raw} when enabled.`;
}

export default function AbandonedReminderSettings() {
  const navigate = useNavigate();
  const message = useMessage();
  const bannerFileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testBlasting, setTestBlasting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [newBannerName, setNewBannerName] = useState("");
  const [cronSubtitle, setCronSubtitle] = useState("cron schedule unavailable.");
  const [bannerSizeByKey, setBannerSizeByKey] = useState({});

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      abandonedReminderApiService.getSettings(),
      cronConfigService.getCronConfig("abandonedLeadReminders"),
    ])
      .then(([sRes, cRes]) => {
        if (sRes?.status?.code === 200 && sRes?.data) {
          setSettings(normalizeSettingsPayload(sRes.data));
        }
        if (cRes?.status?.code === 200 && cRes?.data) {
          setCronSubtitle(formatCronText(cRes.data.schedule, cRes.data.isEnabled));
        }
      })
      .catch(() => message.showToast("Failed to load settings.", ToastTypes.ERROR))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const banners = Array.isArray(settings?.emailBanners) ? settings.emailBanners : [];
    if (!banners.length) {
      setBannerSizeByKey({});
      return undefined;
    }

    let cancelled = false;
    const tasks = banners.map((b, idx) => {
      const key = bannerMetaKey(b, idx);
      const src = bannerPreviewUrl(b?.imageUrl);
      return { key, src };
    });

    Promise.all(
      tasks.map(
        (task) =>
          new Promise((resolve) => {
            if (!task.src) {
              resolve({ key: task.key, width: 0, height: 0 });
              return;
            }
            const img = new window.Image();
            img.onload = () => resolve({ key: task.key, width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
            img.onerror = () => resolve({ key: task.key, width: 0, height: 0 });
            img.src = task.src;
          })
      )
    ).then((entries) => {
      if (cancelled) return;
      const next = {};
      entries.forEach((entry) => {
        if (entry?.key) {
          next[entry.key] = {
            width: Number(entry.width) || 0,
            height: Number(entry.height) || 0,
          };
        }
      });
      setBannerSizeByKey(next);
    });

    return () => {
      cancelled = true;
    };
  }, [settings?.emailBanners]);

  const saveSettings = () => {
    if (!settings) return;
    setSaving(true);
    abandonedReminderApiService
      .updateSettings({
        enabled: !!settings.enabled,
        firstDelayValue: Number(settings.firstDelayValue) || 24,
        firstDelayUnit: settings.firstDelayUnit === "minutes" ? "minutes" : "hours",
        repeatIntervalHours: Number(settings.repeatIntervalHours) || 72,
        maxEmailsPerLead: Number(settings.maxEmailsPerLead) || 3,
        maxProductsInEmail: Math.min(30, Math.max(1, Number(settings.maxProductsInEmail) || 8)),
        emailBanners: settings.emailBanners || [],
        selectedCartBannerId: settings.selectedCartBannerId || "",
        selectedPaymentBannerId: settings.selectedPaymentBannerId || "",
        cartIntroMessage: String(settings.cartIntroMessage || ""),
        paymentIntroMessage: String(settings.paymentIntroMessage || ""),
        cartEmailSubject: String(settings.cartEmailSubject || ""),
        paymentEmailSubject: String(settings.paymentEmailSubject || ""),
        preCtaMessage: String(settings.preCtaMessage || ""),
      })
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast("Saved.", ToastTypes.SUCCESS);
          setSettings(normalizeSettingsPayload(res.data));
        } else {
          message.showToast(res?.error?.message || "Save failed.", ToastTypes.ERROR);
        }
      })
      .catch(() => message.showToast("Save failed.", ToastTypes.ERROR))
      .finally(() => setSaving(false));
  };

  const persistBannerSelection = (nextBanners, nextSelectedCartId, nextSelectedPaymentId, successText = "Saved.") => {
    setSaving(true);
    abandonedReminderApiService
      .updateSettings({
        emailBanners: nextBanners || [],
        selectedCartBannerId: nextSelectedCartId || "",
        selectedPaymentBannerId: nextSelectedPaymentId || "",
      })
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setSettings(normalizeSettingsPayload(res.data));
          if (successText) message.showToast(successText, ToastTypes.SUCCESS);
        } else {
          message.showToast(res?.error?.message || "Save failed.", ToastTypes.ERROR);
          loadAll();
        }
      })
      .catch(() => {
        message.showToast("Save failed.", ToastTypes.ERROR);
        loadAll();
      })
      .finally(() => setSaving(false));
  };

  const sendTestToAllLeads = () => {
    if (!settings) return;
    if (!window.confirm("Send reminder to all open leads now? (No delay rules; counts unchanged.)")) return;
    setTestBlasting(true);
    abandonedReminderApiService
      .sendTestToAllOpenLeads()
      .then((res) => {
        if (res?.status?.code === 200) {
          const d = res.data || {};
          if (d.blocked) {
            message.showToast(d.message || "Reminder emails are disabled.", ToastTypes.ERROR);
            return;
          }
          const cap = d.capped ? ` (max ${d.scanned}/${d.totalOpenLeads})` : "";
          message.showToast(`Sent ${d.sent ?? 0}, skipped ${d.skippedNoEmail ?? 0}, failed ${d.sendFailed ?? 0}${cap}`, ToastTypes.INFO);
        } else {
          message.showToast(res?.error?.message || "Failed.", ToastTypes.ERROR);
        }
      })
      .catch(() => message.showToast("Failed.", ToastTypes.ERROR))
      .finally(() => setTestBlasting(false));
  };

  const onBannerFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("banner", file);
    fd.append("name", String(newBannerName || "").trim());
    abandonedReminderApiService
      .uploadBanner(fd)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          message.showToast("Uploaded.", ToastTypes.SUCCESS);
          setSettings(normalizeSettingsPayload(res.data));
          setNewBannerName("");
        } else {
          message.showToast(res?.error?.message || "Upload failed.", ToastTypes.ERROR);
        }
      })
      .catch(() => message.showToast("Upload failed.", ToastTypes.ERROR))
      .finally(() => {
        setUploading(false);
        if (bannerFileRef.current) bannerFileRef.current.value = "";
      });
  };

  if (loading) {
    return (
      <div className="ar-page">
        <p>Loading…</p>
      </div>
    );
  }

  const removeBannerAt = (idx) => {
    if (!settings) return;
    const list = Array.isArray(settings.emailBanners) ? [...settings.emailBanners] : [];
    const removed = list[idx];
    list.splice(idx, 1);
    const removedId = removed?._id != null ? String(removed._id) : "";
    const cartSel = String(settings.selectedCartBannerId || "");
    const paySel = String(settings.selectedPaymentBannerId || "");
    const nextCartSel = removedId && cartSel === removedId ? "" : cartSel;
    const nextPaySel = removedId && paySel === removedId ? "" : paySel;
    // Optimistic UI update, then persist server-side so refresh keeps deletion.
    setSettings((s) => ({ ...s, emailBanners: list, selectedCartBannerId: nextCartSel, selectedPaymentBannerId: nextPaySel }));
    persistBannerSelection(list, nextCartSel, nextPaySel, "Banner removed.");
  };

  const onBannerApplyToChange = (idx, nextApplyTo) => {
    setSettings((s) => {
      const list = Array.isArray(s.emailBanners) ? [...s.emailBanners] : [];
      const row = { ...(list[idx] || {}) };
      row.applyTo = nextApplyTo;
      list[idx] = row;
      const id = String(row?._id || "");
      let cartId = String(s.selectedCartBannerId || "");
      let payId = String(s.selectedPaymentBannerId || "");
      if (id && nextApplyTo === "cart" && payId === id) payId = "";
      if (id && nextApplyTo === "payment" && cartId === id) cartId = "";
      return { ...s, emailBanners: list, selectedCartBannerId: cartId, selectedPaymentBannerId: payId };
    });
  };

  const setBannerActiveByType = (banner) => {
    if (!banner?._id) return;
    const id = String(banner._id);
    const applyTo = String(banner.applyTo || "both");
    let nextCart = String(settings?.selectedCartBannerId || "");
    let nextPayment = String(settings?.selectedPaymentBannerId || "");
    if (applyTo === "cart") {
      nextCart = id;
      if (nextPayment === id) nextPayment = "";
    } else if (applyTo === "payment") {
      nextPayment = id;
      if (nextCart === id) nextCart = "";
    } else {
      nextCart = id;
      nextPayment = id;
    }
    setSettings((s) => ({ ...s, selectedCartBannerId: nextCart, selectedPaymentBannerId: nextPayment }));
    persistBannerSelection(settings?.emailBanners || [], nextCart, nextPayment, "Banner activation updated.");
  };

  return (
    <div className="ar-page">
      <div className="ar-top">
        <button type="button" className="ar-back" onClick={() => navigate("/admin/abandoned-leads/cart")}>
          ← Abandoned leads
        </button>
        <h1>Reminder emails</h1>
        <p className="ar-sub">Cart / payment abandoned leads · {cronSubtitle}</p>
      </div>

      <section className="ar-card">
        <h2>Timing &amp; limits</h2>
        <label className="ar-row">
          <input
            type="checkbox"
            checked={!!settings?.enabled}
            onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
          />
          <span>Enable automatic reminders</span>
        </label>
        <p className="ar-help" style={{ marginTop: -4, marginBottom: 10 }}>
          When disabled, no reminder email is sent (cron and test send are both blocked).
        </p>
        <div className="ar-grid">
          <label>
            First email after
            <input
              type="number"
              min={1}
              value={settings?.firstDelayValue ?? 24}
              onChange={(e) => setSettings((s) => ({ ...s, firstDelayValue: e.target.value }))}
            />
          </label>
          <label>
            Unit
            <select
              value={settings?.firstDelayUnit || "hours"}
              onChange={(e) => setSettings((s) => ({ ...s, firstDelayUnit: e.target.value }))}
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
            </select>
          </label>
          <label>
            Min hours between follow-ups
            <input
              type="number"
              min={1}
              value={settings?.repeatIntervalHours ?? 72}
              onChange={(e) => setSettings((s) => ({ ...s, repeatIntervalHours: e.target.value }))}
            />
          </label>
          <label>
            Max emails per open lead
            <input
              type="number"
              min={1}
              max={20}
              value={settings?.maxEmailsPerLead ?? 3}
              onChange={(e) => setSettings((s) => ({ ...s, maxEmailsPerLead: e.target.value }))}
            />
          </label>
          <label>
            Max items shown in email
            <input
              type="number"
              min={1}
              max={30}
              value={settings?.maxProductsInEmail ?? 8}
              onChange={(e) => setSettings((s) => ({ ...s, maxProductsInEmail: e.target.value }))}
            />
          </label>
        </div>
        <div className="ar-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          <label>
            Cart email subject
            <input
              type="text"
              maxLength={200}
              value={settings?.cartEmailSubject || ""}
              onChange={(e) => setSettings((s) => ({ ...s, cartEmailSubject: e.target.value }))}
            />
          </label>
          <label>
            Payment email subject
            <input
              type="text"
              maxLength={200}
              value={settings?.paymentEmailSubject || ""}
              onChange={(e) => setSettings((s) => ({ ...s, paymentEmailSubject: e.target.value }))}
            />
          </label>
        </div>
        <div className="ar-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          <label>
            Cart email message
            <textarea
              rows={3}
              value={settings?.cartIntroMessage || ""}
              onChange={(e) => setSettings((s) => ({ ...s, cartIntroMessage: e.target.value }))}
            />
          </label>
          <label>
            Payment email message
            <textarea
              rows={3}
              value={settings?.paymentIntroMessage || ""}
              onChange={(e) => setSettings((s) => ({ ...s, paymentIntroMessage: e.target.value }))}
            />
          </label>
        </div>
        <div className="ar-grid" style={{ gridTemplateColumns: "1fr" }}>
          <label>
            Optional message above "Complete Your Order" button (leave empty to hide)
            <textarea
              rows={3}
              value={settings?.preCtaMessage || ""}
              onChange={(e) => setSettings((s) => ({ ...s, preCtaMessage: e.target.value }))}
            />
          </label>
        </div>
        <div className="ar-actions">
          <button type="button" className="ar-btn primary" disabled={saving} onClick={saveSettings}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="ar-btn ghost" disabled={testBlasting || !settings?.enabled} onClick={sendTestToAllLeads}>
            {testBlasting ? "Sending…" : settings?.enabled ? "Test email all open leads" : "Enable reminders to test send"}
          </button>
        </div>
      </section>

      <section className="ar-card">
        <h2>Email banner</h2>
        <input
          ref={bannerFileRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          style={{ display: "none" }}
          onChange={onBannerFileChosen}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
          <div style={{ flex: "1 1 320px", minWidth: 220 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "0.78rem", color: "#334155" }}>
              Label (optional)
              <input
                value={newBannerName}
                onChange={(e) => setNewBannerName(e.target.value)}
                placeholder="Internal name"
                style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: "0.82rem" }}
              />
            </label>
          </div>
          <button type="button" className="ar-btn ghost" onClick={() => bannerFileRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload Banner"}
          </button>
          <button type="button" className="ar-btn primary" disabled={saving} onClick={saveSettings}>
            Save selection
          </button>
        </div>
        <p className="ar-help" style={{ marginTop: 0 }}>
          Max 3&nbsp;MB, up to 20 files. Choose banner type and click <strong>Set active</strong> (Cart/Payment/Both).
        </p>
        <ul className="ar-tpl-list">
          {(settings?.emailBanners || []).length ? (
            settings.emailBanners.map((b, idx) => {
              const id = String(b?._id || "");
              const metaKey = bannerMetaKey(b, idx);
              const cartActive = String(settings?.selectedCartBannerId || "") === id && !!id;
              const paymentActive = String(settings?.selectedPaymentBannerId || "") === id && !!id;
              const src = bannerPreviewUrl(b.imageUrl);
              const sizeMeta = bannerSizeByKey[metaKey] || {};
              const sizeText =
                Number(sizeMeta.width) > 0 && Number(sizeMeta.height) > 0
                  ? `${sizeMeta.width} x ${sizeMeta.height}px`
                  : "Size detecting...";
              const feedback = getBannerDimensionFeedback(sizeMeta);
              return (
                <li key={id || `tmp-${idx}-${b.imageUrl}`} className="ar-tpl-item">
                  <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                    <div
                      style={{
                        width: 100,
                        height: 56,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        flex: "0 0 auto",
                        background: "#f8fafc",
                      }}
                    >
                      {src ? (
                        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : null}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.name || "Banner"}
                        {cartActive ? <span className="ar-badge">Cart active</span> : null}
                        {paymentActive ? <span className="ar-badge">Payment active</span> : null}
                        {!id ? <span className="ar-badge muted">Pending save</span> : null}
                      </div>
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.imageUrl}
                      </div>
                      <div className="ar-banner-size-row">
                        <span className="ar-banner-size-badge">{sizeText}</span>
                        <span className={`ar-banner-size-status ${feedback.tone}`}>{feedback.text}</span>
                        <span className="ar-banner-size-hint">Recommended: 1104 x 360px</span>
                      </div>
                    </div>
                  </div>
                  <div className="ar-tpl-actions">
                    <select
                      className="ar-banner-select"
                      value={b.applyTo || "both"}
                      onChange={(e) => onBannerApplyToChange(idx, e.target.value)}
                    >
                      <option value="cart">Cart</option>
                      <option value="payment">Payment</option>
                      <option value="both">Both</option>
                    </select>
                    <button
                      type="button"
                      className="ar-btn ar-btn-mini ar-btn-outline"
                      onClick={() => setBannerActiveByType(b)}
                      disabled={!id}
                    >
                      Set active
                    </button>
                    <button type="button" className="ar-btn ar-btn-mini ar-btn-danger-outline" onClick={() => removeBannerAt(idx)}>
                      Remove
                    </button>
                  </div>
                </li>
              );
            })
          ) : (
            <li className="ar-tpl-item" style={{ borderBottom: "none", color: "#64748b", fontSize: "0.82rem" }}>
              No uploads yet. If no banner is selected, email sends without a banner.
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
